import type { ReactNode } from "react";
import React, { createContext, useContext, useState, useEffect } from "react";
import Toast from "./Toast";

interface ToastData {
  id: number;
  message: string;
  type: "success" | "error";
  onTryAgain?: () => void;
}

interface PendingToastState {
  message: string;
  type: "success" | "error";
  onTryAgain?: () => void;
}

interface ToastContextProps {
  setPendingToast: (
    message: string,
    type?: "success" | "error",
    onTryAgain?: () => void,
  ) => void;
}

const ToastContext = createContext<ToastContextProps | undefined>(undefined);

export const ToastProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [toasts, setToasts] = useState<ToastData[]>([]);
  const [pendingToastState, setPendingToastState] =
    useState<PendingToastState | null>(null);

  const showToast = (
    message: string,
    type: "success" | "error" = "success",
    onTryAgain?: () => void,
  ) => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type, onTryAgain }]);

    setTimeout(() => {
      setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, 5000);
  };

  const setPendingToast = (
    message: string,
    type: "success" | "error" = "success",
    onTryAgain?: () => void,
  ) => {
    setPendingToastState({ message, type, onTryAgain });
  };

  useEffect(() => {
    if (pendingToastState) {
      showToast(
        pendingToastState.message,
        pendingToastState.type,
        pendingToastState.onTryAgain,
      );
      setPendingToastState(null);
    }
  }, [pendingToastState]);

  return (
    <ToastContext.Provider value={{ setPendingToast }}>
      {children}
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onTryAgain={toast.onTryAgain}
          onClose={() =>
            setToasts((prev) => prev.filter((t) => t.id !== toast.id))
          }
          blurBackground={toast.type === "error"}
        />
      ))}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};
